import { useUpdate } from "@refinedev/core";
import { CollapsibleSection } from "components/collapsibleSection";
import { ConfirmModal } from "components/confirmModal";
import { FullScreenLoading } from "components/fullScreenLoading";
import Select from "components/select";
import {
  IOrderDetail,
  OrderStatusKey,
  PaymentMethodKey,
  ReadonlyPaymentMethodkey,
} from "interfaces";
import moment from "moment";
import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
  ADMIN_USD_PAYMENT_METHODS,
  CONTENT_ORDER_PAYMENT_METHODS,
  EDIT_ORDER_STATUS_LIST,
  ORDER_PAYMENT_METHODS,
  PAYMENT_METHODS,
} from "utills/constants";
import {
  convertCurrency,
  currencySymbol,
  formatNumber,
  getOrderStatusLabel,
  getOrderTypeLabel,
  getPaymentMethodLabel,
  getPaymentStatusTypeByOrderStatus,
  getTicketPlatformLabel,
} from "utills/helpers";

const INITIAL_MODAL_CONFIG = {
  open: false,
  title: "",
  submit: () => {},
};

export const EditOrderSection = ({
  orderDetailData,
  rate,
  refetch,
}: {
  orderDetailData: IOrderDetail | null;
  rate: number;
  refetch: () => void;
}) => {
  const [confirmModalConfig, setConfirmModalConfig] =
    useState(INITIAL_MODAL_CONFIG);
  const { mutate, isLoading: isUpdatingStatus } = useUpdate();

  const _onCloseConfirmModal = () => {
    setConfirmModalConfig({
      ...INITIAL_MODAL_CONFIG,
      title: confirmModalConfig.title,
    });
  };

  const _updatePaymentMethod = (method: string) => {
    mutate(
      {
        resource: `checkout/orders`,
        id: `${orderDetailData?.code}/payment-method`,
        meta: {
          ignoreResourceWrapper: true,
        },
        values: {
          payment_method: method ?? "",
          transaction_id: orderDetailData?.transaction?.id ?? "",
        },
      },
      {
        onSuccess: () => {
          toast.success("Update successfully");
          refetch();
        },
      }
    );
  };

  const _updateOrderStatus = (status: string) => {
    mutate(
      {
        resource: `checkout/orders`,
        id: `${orderDetailData?.code}/order-status`,
        meta: {
          ignoreResourceWrapper: true,
        },
        values: {
          payment_status:
            getPaymentStatusTypeByOrderStatus(status as OrderStatusKey) ?? "",
          transaction_id: orderDetailData?.transaction?.id ?? "",
        },
      },
      {
        onSuccess: () => {
          toast.success("Update successfully");
          refetch();
        },
      }
    );
  };

  const isReadyOnlyMethod = (
    Object.values(ReadonlyPaymentMethodkey) as any
  ).includes(orderDetailData?.transaction?.parent_payment_method)
    ? true
    : false;
  const filteredOrderrStatus =
    orderDetailData?.order_status === OrderStatusKey.PENDING
      ? EDIT_ORDER_STATUS_LIST.filter(
          (status) => status.value !== OrderStatusKey.REFUNDED
        ) // no refund option for pending status
      : EDIT_ORDER_STATUS_LIST.filter(
          (status) => status.value !== orderDetailData?.order_status
        );

  const PAYMENT_METHOD_CURRENCY =
    useMemo(() => {
      if (orderDetailData?.currency === "vnd") return PAYMENT_METHODS;
      if (orderDetailData?.currency === "usd") return ADMIN_USD_PAYMENT_METHODS;
    }, [orderDetailData?.currency])?.filter((method) => {
      return method?.value !== PaymentMethodKey.ALL;
    }) ?? [];

  return (
    <>
      <CollapsibleSection label="Order Information" defaultOpen={true}>
        <div
          className="grid gap-x-4 gap-y-6 px-4 py-4 text-sm items-center [&_*]:items-center sm:px-6 grid-cols-12 lg:gap-x-6"
          style={{ wordBreak: "break-word" }}
        >
          <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-1">
            <div className="col-span-2">
              <p className="font-medium">Order status</p>
            </div>
            <div className="col-span-4">
              {/* <Tag text={getOrderStatusLabel(orderDetailData?.order_status ?? "")} type={orderDetailData?.order_status}  /> */}
              <div className="w-60 max-w-full">
                <Select
                  options={filteredOrderrStatus}
                  id="order_status"
                  placeholder="Select Order Status"
                  className="py-0.5"
                  value={
                    EDIT_ORDER_STATUS_LIST.find(
                      (selected: { label: string; value: string }) =>
                        selected.value === orderDetailData?.order_status
                    ) ??
                    getOrderStatusLabel(orderDetailData?.order_status ?? "")
                      ? {
                          label: getOrderStatusLabel(
                            orderDetailData?.order_status ?? ""
                          ),
                          value: orderDetailData?.order_status,
                        }
                      : ""
                  }
                  onChange={(selected: { label: string; value: string }) => {
                    if (selected?.value !== orderDetailData?.order_status) {
                      setConfirmModalConfig({
                        open: true,
                        title: `Are you sure you want to change status to ${getOrderStatusLabel(
                          selected?.value ?? ""
                        )}?`,
                        submit: () => _updateOrderStatus(selected?.value),
                      });
                    }
                  }}
                />
              </div>
            </div>
          </div>
          <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-3">
            <div className="col-span-2">
              <p className="font-medium">Order price</p>
            </div>
            <div className="col-span-4">
              <p className="text-brand-secondary-500 font-semibold">
                {currencySymbol()}{" "}
                {convertCurrency(
                  orderDetailData?.grand_total ?? 0,
                  orderDetailData?.currency ?? "",
                  rate
                )}
              </p>
            </div>
          </div>
          <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-5">
            <div className="col-span-2">
              <p className="font-medium">Total discount amount</p>
            </div>
            <div className="col-span-4">
              <p>
                {currencySymbol()}{" "}
                {convertCurrency(
                  orderDetailData?.discount_coupon ?? 0,
                  orderDetailData?.currency ?? "",
                  rate
                )}
              </p>
            </div>
          </div>
          <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-7">
            <div className="col-span-2">
              <p className="font-medium">Payment unit</p>
            </div>
            <div className="col-span-4">
              <p className="uppercase">{orderDetailData?.currency}</p>
            </div>
          </div>
          <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-9">
            <div className="col-span-2">
              <p className="font-medium">Payment method</p>
            </div>
            <div className="col-span-4">
              {isReadyOnlyMethod ||
              orderDetailData?.order_status === OrderStatusKey.SUCCESSFUL ? (
                <p>
                  {getPaymentMethodLabel(
                    orderDetailData?.transaction?.parent_payment_method ?? ""
                  )}
                </p>
              ) : (
                <div className="w-60 max-w-full">
                  <Select
                    options={PAYMENT_METHOD_CURRENCY}
                    id="payment_method"
                    placeholder="Select Payment Method"
                    className="py-0.5"
                    value={PAYMENT_METHOD_CURRENCY.find(
                      (d) =>
                        d.value ===
                        orderDetailData?.transaction?.parent_payment_method
                    )}
                    onChange={(selected: { label: string; value: string }) => {
                      if (
                        selected?.value !==
                        orderDetailData?.transaction?.parent_payment_method
                      ) {
                        setConfirmModalConfig({
                          open: true,
                          title: `Are you sure you want to change method to ${getPaymentMethodLabel(
                            selected?.value
                          )}?`,
                          submit: () => _updatePaymentMethod(selected?.value),
                        });
                      }
                    }}
                  />
                </div>
              )}
            </div>
          </div>
          <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-11">
            <div className="col-span-2">
              <p className="font-medium">Transaction code</p>
            </div>
            <div className="col-span-4">
              <p>{orderDetailData?.transaction?.reference_number}</p>
            </div>
          </div>
          <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-[13]">
            <div className="col-span-2">
              <p className="font-medium">Event name</p>
            </div>
            <div className="col-span-4">
              <p>{orderDetailData?.event_name_en}</p>
            </div>
          </div>
          <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-[15]">
            <div className="col-span-2">
              <p className="font-medium">Total discount promotion</p>
            </div>
            <div className="col-span-4">
              <p>
                {currencySymbol()}{" "}
                {convertCurrency(
                  orderDetailData?.discount_promotion ?? 0,
                  orderDetailData?.currency ?? "",
                  rate
                )}
              </p>
            </div>
          </div>
          <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-[17]">
            <div className="col-span-2">
              <p className="font-medium">Order type</p>
            </div>
            <div className="col-span-4">
              <p>{getOrderTypeLabel(orderDetailData?.order_type ?? "")}</p>
            </div>
          </div>
          <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-[19]">
            <div className="col-span-2">
              <p className="font-medium">Ticket platform</p>
            </div>
            <div className="col-span-4">
              <p>
                {getTicketPlatformLabel(orderDetailData?.ticket_platform ?? "")}
              </p>
            </div>
          </div>
          <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-2">
            <div className="col-span-2">
              <p className="font-medium">Created at</p>
            </div>
            <div className="col-span-4">
              <p>
                {orderDetailData?.created_at
                  ? moment(orderDetailData.created_at).format(
                      "DD/MM/YYYY HH:mm:ss"
                    )
                  : ""}
              </p>
            </div>
          </div>
          <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-4">
            <div className="col-span-2">
              <p className="font-medium">Payment at</p>
            </div>
            <div className="col-span-4">
              <p>
                {orderDetailData?.purchased_at
                  ? moment(orderDetailData.purchased_at).format(
                      "DD/MM/YYYY HH:mm:ss"
                    )
                  : ""}
              </p>
            </div>
          </div>
          <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-6">
            <div className="col-span-2">
              <p className="font-medium">Total items</p>
            </div>
            <div className="col-span-4">
              <p>{formatNumber(orderDetailData?.total_item ?? 0)}</p>
            </div>
          </div>
          <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-8">
            <div className="col-span-2">
              <p className="font-medium">Tickets name</p>
            </div>
            <div className="col-span-4">
              <p>{orderDetailData?.item_description_en}</p>
            </div>
          </div>
          <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-10">
            <div className="col-span-2">
              <p className="font-medium">E-ticket usage date</p>
            </div>
            <div className="col-span-4">
              <p>
                {orderDetailData?.ticket_use_for_date
                  ? moment(orderDetailData.ticket_use_for_date).format(
                      "DD/MM/YYYY"
                    )
                  : ""}
                {orderDetailData?.showtime
                  ? ` - ${orderDetailData.showtime}`
                  : ""}
              </p>
            </div>
          </div>
          <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-12">
            <div className="col-span-2">
              <p className="font-medium">Group name</p>
            </div>
            <div className="col-span-4">
              <p>{orderDetailData?.group_name}</p>
            </div>
          </div>
          <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-[14]">
            <div className="col-span-2">
              <p className="font-medium">Org name</p>
            </div>
            <div className="col-span-4">
              <p>{orderDetailData?.organization?.name_en}</p>
            </div>
          </div>

          <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-[16]">
            <div className="col-span-2">
              <p className="font-medium">Total participants</p>
            </div>
            <div className="col-span-4">
              <p>{formatNumber(orderDetailData?.total_attendant ?? 0)}</p>
            </div>
          </div>
          <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-[18]">
            <div className="col-span-2">
              <p className="font-medium">Partner order code</p>
            </div>
            <div className="col-span-4">
              {/* <p>
                
              </p> */}
            </div>
          </div>
          <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-[20]">
            <div className="col-span-2">
              <p className="font-medium">Private flow</p>
            </div>
            <div className="col-span-4">
              <p>{orderDetailData?.auth_identifier}</p>
            </div>
          </div>
        </div>
      </CollapsibleSection>

      <ConfirmModal
        key="change-status"
        title={confirmModalConfig.title}
        content=""
        open={confirmModalConfig.open}
        onConfirm={confirmModalConfig.submit}
        onClose={_onCloseConfirmModal}
      />
      {isUpdatingStatus ? <FullScreenLoading /> : <></>}
    </>
  );
};
